import React, { useEffect, useState } from "react";
import {
  Box, Typography, TextField, Button, Grid, CircularProgress,
  FormControl, InputLabel, Select, MenuItem,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { fetchAction, updateAction, fetchDepartmentTasks } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";

export default function DepartmentActionEditPage() {
  const { businessDomainId, businessUnitId, departmentId, actionId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const detailPath = `/strategy/${businessDomainId}/business-units/${businessUnitId}/departments/${departmentId}/actions/${actionId}`;

  const [original, setOriginal] = useState(null);
  const [form, setForm] = useState({ actionName: "", actionDescription: "" });
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [selectedTaskId, setSelectedTaskId] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [act, taskList] = await Promise.all([
          fetchAction(actionId),
          fetchDepartmentTasks(departmentId),
        ]);
        setOriginal(act);
        setForm({
          actionName: act.actionName || "",
          actionDescription: act.actionDescription || "",
        });
        setTasks(taskList || []);
        setSelectedTaskId(act.taskId || "");
      } catch (e) { notify.apiError(e); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionId, departmentId]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const selectedTask = tasks.find((tk) => tk.taskId === selectedTaskId);
    try {
      await updateAction({
        ...original,
        taskId: selectedTask ? selectedTask.taskId : original.taskId,
        taskName: selectedTask ? selectedTask.taskName : original.taskName,
        actionName: form.actionName,
        actionDescription: form.actionDescription || null,
      });
      notify.success(t("message.update.success"));
      navigate(detailPath);
    } catch (err) { notify.apiError(err); }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!original) return null;

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>{t("department.action.edit.title")}</Typography>
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("department.action.task")}</InputLabel>
              <Select
                value={selectedTaskId}
                onChange={(e) => setSelectedTaskId(e.target.value)}
                label={t("department.action.task")}
              >
                {tasks.map((task) => (
                  <MenuItem key={task.taskId} value={task.taskId}>{task.taskName}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth
              required
              label={t("department.action.name")}
              name="actionName"
              value={form.actionName}
              onChange={handleChange}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label={t("common.description")}
              name="actionDescription"
              value={form.actionDescription}
              onChange={handleChange}
            />
          </Grid>
        </Grid>
        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit">{t("common.save")}</Button>
          <Button variant="outlined" onClick={() => navigate(detailPath)}>
            {t("common.cancel")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
